"use client";

import { ThumbsUp, TriangleAlert } from "lucide-react";
import useTranslation from "next-translate/useTranslation";
import { Button } from "./button";
import { Dialog } from "./dialog";

interface ConfirmDialogV2Props {
  readonly isOpen: boolean;
  readonly setIsOpen: (value: boolean) => void;
  readonly title: string;
  readonly description: string;
  readonly descriptionBold?: string;
  readonly action: (() => void) | (() => Promise<void>);
  readonly descriptionBoldClassName?: string;
  readonly variant?: "warn" | "approve";
  readonly confirmText?: string;
  readonly confirmBgColor?: string;
  readonly actionDisabled?: boolean;
}

export default function ConfirmDialogV2({
  isOpen,
  setIsOpen,
  action,
  title,
  description,
  descriptionBold,
  descriptionBoldClassName,
  variant = "warn",
  confirmText,
  confirmBgColor,
  actionDisabled = false
}: ConfirmDialogV2Props) {
  const { t } = useTranslation("common");

  const handleAction = async () => {
    try {
      await action();
      setIsOpen(false);
    } catch (error) {
      console.error("Error al ejecutar la acción:", error);
    }
  };

  return (
    <Dialog
      open={isOpen}
      onClose={() => setIsOpen(false)}
      title={title}
      description=""
      size="sm"
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={() => setIsOpen(false)}>
            {t("Cancelar")}
          </Button>
          <Button
            variant={variant === "approve" ? "primary" : "destructive"}
            className={variant === "approve" && confirmBgColor ? confirmBgColor : undefined}
            onClick={handleAction}
            disabled={actionDisabled}
          >
            {variant === "approve"
              ? (confirmText ?? t("Aceptar"))
              : (confirmText ?? t("Eliminar"))}
          </Button>
        </div>
      }
    >
      <div className="flex items-start gap-3">
        {variant === "warn" ? (
          <TriangleAlert className="mt-0.5 h-5 w-5 shrink-0 text-orange-500" />
        ) : (
          <ThumbsUp className="mt-0.5 h-5 w-5 shrink-0 text-indigo-500" />
        )}
        <p className="text-sm text-slate-600 dark:text-zinc-300">
          {description}{" "}
          {descriptionBold ? (
            <span className={`${descriptionBoldClassName ?? ""} font-semibold`}>
              {descriptionBold}
            </span>
          ) : null}
        </p>
      </div>
    </Dialog>
  );
}
